import axios from 'axios';
import { 
  PaginatedResponse, 
  Product, 
  Category, 
  StoreProfile,
  OrderData,
  Order,
} from '../types/api';

// Get the API URL from environment variables or default if not set
const API_URL = process.env.REACT_APP_API_URL;
console.log("Using API URL:", API_URL);

// Set up axios with base URL
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000'),
});

// Add auth token to requests if available
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response) {
      return Promise.reject(error);
    }
    
    const originalRequest = error.config;
    
    // If error is 401 and we haven't tried to refresh the token yet
    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          // No refresh token available, clear storage
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          // Force reload to login page
          window.location.href = '/login';
          return Promise.reject(error);
        }
        
        // Call refresh token endpoint
        const response = await axios.post(`${API_URL}/auth/refresh`, { 
          refresh_token: refreshToken 
        });
        
        // Store new tokens
        const { access_token, refresh_token } = response.data;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        
        // Update auth header for the original request
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
        
        // Retry the original request
        return axios(originalRequest);
      } catch (refreshError) {
        // If refresh fails, logout the user
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        // Force reload to login page
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  }
);

// Available sort options
export const sortOptions = [
  { value: "ADDED_TIME_DESC", label: "Newest First" },
  { value: "ADDED_TIME_ASC", label: "Oldest First" },
  { value: "PRICE_ASC", label: "Price: Low to High" },
  { value: "PRICE_DESC", label: "Price: High to Low" },
  { value: "NAME_ASC", label: "Name: A to Z" },
  { value: "NAME_DESC", label: "Name: Z to A" },
  { value: "UPDATED_TIME_DESC", label: "Recently Updated" },
  { value: "POPULARITY_DESC", label: "Most Popular" }
];

// Product filter interface
export interface ProductFilterOptions {
  limit?: number;
  offset?: number;
  category?: number;
  keyword?: string;
  sortBy?: string;
  enabled?: boolean;
  inStock?: boolean;
  inventoryTracking?: boolean;
  onSale?: boolean;
  priceFrom?: number;
  priceTo?: number;
  productClassId?: number;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  weightFrom?: number;
  weightTo?: number;
  sku?: string;
  attributes?: Record<string, string>; // attribute[id]=value
  options?: Record<string, string>;    // option[name]=value
}

// Default parameters for product queries
const defaultProductParams: ProductFilterOptions = {
  enabled: true,         // Only show enabled products
  // inStock: true,        // Removed to show out-of-stock items with visual indicators
  priceFrom: 0.02,      // Include sale products (was 1, now 0.02 to include $0.99 items but exclude 0.01 items like recycle fee and eco fee unit)
  priceTo: 1000,        // Reasonable max price
  limit: 50,             // Show more products by default
  offset: 0
};

// Convert filter options to API query parameters
const convertFiltersToParams = (filters: ProductFilterOptions): Record<string, any> => {
  const params: Record<string, any> = {};

  // Copy all base filters
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && !['attributes', 'options'].includes(key)) {
      // Convert key from camelCase to snake_case
      const paramKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      params[paramKey] = value;
    }
  });

  // Handle attributes and options
  if (filters.attributes) {
    Object.entries(filters.attributes).forEach(([id, value]) => {
      params[`attribute_${id}`] = value;
    });
  }

  if (filters.options) {
    Object.entries(filters.options).forEach(([name, value]) => {
      params[`option_${name}`] = value;
    });
  }

  return params;
};

// Define a more detailed ProductFilters interface to match Ecwid API
export interface ProductFilters {
  productCount: number;
  filters: {
    price?: {
      minValue: number;
      maxValue: number;
    };
    inventory?: Array<{
      id: string;
      title: string;
      titleTranslated?: string;
      productCount: number;
    }>;
    onsale?: Array<{
      id: string;
      title: string;
      titleTranslated?: string;
      productCount: number;
    }>;
    categories?: Array<{
      id: number;
      title: string;
      titleTranslated?: string;
      productCount: number;
    }>;
    attributes?: {
      [attributeName: string]: Array<{
        id: string;
        title: string;
        titleTranslated?: string;
        productCount: number;
      }>;
    };
    options?: {
      [optionName: string]: Array<{
        id: string;
        title: string;
        titleTranslated?: string;
        productCount: number;
      }>;
    };
  };
}

// API Services
export const productsApi = {
  // Get all products with pagination and filtering
  getProducts: async (filters: ProductFilterOptions = {}): Promise<PaginatedResponse<Product>> => {
    const mergedFilters = { ...defaultProductParams, ...filters };
    const params = convertFiltersToParams(mergedFilters);
    
    const response = await apiClient.get<PaginatedResponse<Product>>('/products', { params });
    return response.data;
  },

  // Get a single product by ID
  getProduct: async (id: number): Promise<Product> => {
    try {
      console.log(`Fetching product with ID: ${id}`);
      const response = await apiClient.get<Product>(`/products/${id}`);
      console.log(`Product data received:`, response.data);
      return response.data;
    } catch (error: any) {
      console.error(`Error fetching product ${id}:`, error);
      // Create a more descriptive error with details
      const errorMsg = error.response?.status === 404 
        ? `Product with ID ${id} not found` 
        : `Failed to fetch product details. Please try again later.`;
      throw new Error(errorMsg);
    }
  },

  // Search products by keyword
  searchProducts: async (keyword: string, filters: ProductFilterOptions = {}): Promise<PaginatedResponse<Product>> => {
    return productsApi.getProducts({ ...filters, keyword });
  },

  // Get products by category
  getProductsByCategory: async (categoryId: number, filters: ProductFilterOptions = {}): Promise<PaginatedResponse<Product>> => {
    return productsApi.getProducts({ ...filters, category: categoryId });
  },
  
  // Get products by keyword and category
  searchProductsByCategory: async (keyword: string, categoryId: number, filters: ProductFilterOptions = {}): Promise<PaginatedResponse<Product>> => {
    return productsApi.getProducts({ ...filters, keyword, category: categoryId });
  },
  
  // Get available product filters
  getProductFilters: async (categoryIds?: number[]): Promise<ProductFilters> => {
    try {
      let url = '/products/filters';
      const response = await apiClient.post<ProductFilters>(url, { categories: categoryIds });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching product filters:', error);
      throw new Error('Failed to fetch product filters. Please try again later.');
    }
  },

  // Get user's favorites
  getFavorites: async (): Promise<number[]> => {
    try {
      const response = await apiClient.get<number[]>('/favorites/ids');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching favorites:', error);
      return [];
    }
  },

  // Check if a product is in user's favorites
  isFavorite: async (productId: number): Promise<boolean> => {
    try {
      const response = await apiClient.get<boolean>(`/favorites/check/${productId}`);
      return response.data;
    } catch (error: any) {
      console.error(`Error checking favorite status for product ${productId}:`, error);
      return false;
    }
  },

  // Add a product to favorites
  addFavorite: async (productId: number): Promise<void> => {
    try {
      await apiClient.post('/favorites', { product_id: productId });
    } catch (error: any) {
      console.error(`Error adding product ${productId} to favorites:`, error);
      throw new Error('Failed to add product to favorites. Please try again later.');
    }
  },

  // Remove a product from favorites
  removeFavorite: async (productId: number): Promise<void> => {
    try {
      await apiClient.delete(`/favorites/${productId}`);
    } catch (error: any) {
      console.error(`Error removing product ${productId} from favorites:`, error);
      throw new Error('Failed to remove product from favorites. Please try again later.');
    }
  },

  // Toggle favorite status for a product
  toggleFavorite: async (productId: number, currentStatus: boolean): Promise<boolean> => {
    try {
      if (currentStatus) {
        await productsApi.removeFavorite(productId);
        return false;
      } else {
        await productsApi.addFavorite(productId);
        return true;
      }
    } catch (error: any) {
      console.error(`Error toggling favorite status for product ${productId}:`, error);
      throw new Error('Failed to update favorite status. Please try again later.');
    }
  }
};

export const categoriesApi = {
  // Get all categories
  getCategories: async (): Promise<PaginatedResponse<Category>> => {
    const response = await apiClient.get<PaginatedResponse<Category>>('/categories');
    return response.data;
  },

  // Get a single category by ID
  getCategory: async (id: number): Promise<Category> => {
    const response = await apiClient.get<Category>(`/categories/${id}`);
    return response.data;
  }
};

export const storeApi = {
  // Get store profile
  getStoreProfile: async (): Promise<StoreProfile> => {
    const response = await apiClient.get<StoreProfile>('/store');
    return response.data;
  }
};

// Define a type for API response with orderId
export interface OrderResponse {
  id?: number;
  orderId?: string;
}

export const ordersApi = {
  // Create a new order
  createOrder: async (orderData: OrderData, customHeaders = {}): Promise<OrderResponse> => {
    const url = `${API_URL}/orders`;
    
    const headers = {
      'Content-Type': 'application/json',
      ...customHeaders
    };

    console.log('Sending order to:', url);
    console.log('Order data:', JSON.stringify(orderData));
    
    const response = await axios.post(url, orderData, { headers });
    return response.data;
  },

  // Get orders (will require authentication in a real implementation)
  getOrders: async (limit = 10, offset = 0): Promise<PaginatedResponse<Order>> => {
    const response = await apiClient.get<PaginatedResponse<Order>>('/orders', {
      params: { limit, offset }
    });
    return response.data;
  }
}; 